import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/common-ui/card";
import { Input } from "../../../../ui/common-ui/input";
import { Label } from "../../../../ui/common-ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/common-ui/select";
import { Separator } from "../../../../ui/common-ui/separator";
import { Badge } from "../../../../ui/common-ui/badge";
import { SettingProps } from "../../interface";
import { useToast } from "../../../../ui/common-ui/use-toast";
import {
    fetchTenants,
    githubConfigure,
    githubDeleteConfiguration,
    githubGetConfiguration,
    githubGetInstallUrl,
} from "../../Api/settingsApi";
import { ArrowLeft, CheckCircle, Plus, Trash2, Upload, FileKey2, Link as LinkIcon } from "lucide-react";
import { FaGithub } from "react-icons/fa6";
import { Button } from "../../../../ui/common-ui/button";

type TenantData = {
    tenantName: string;
    domainName?: string;
};

type GithubConfiguration = {
    id?: number | string;
    tenantName: string;
    instanceUrl?: string;
    appId: number;
    appSlug: string;
    organizationName: string;
    webhookSecret?: string;
    setupUrl?: string;
    createdAt?: string;
    updatedAt?: string;
};

type InstallUrlResponse = {
    tenantName: string;
    orgLogin: string;
    installUrl: string;
    instanceUrl: string;
};

type CtaMode = "configure" | "loading" | "connect";

const DEFAULT_INSTANCE_URL = "https://github.com";

export default function GithubSettings({ onBack }: SettingProps) {
    const { toast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [tenants, setTenants] = useState<TenantData[]>([]);
    const [tenantName, setTenantName] = useState<string>("");

    const [configurations, setConfigurations] = useState<GithubConfiguration[]>([]);
    const [selectedConfigId, setSelectedConfigId] = useState<string>("");
    const [loadingConfigurations, setLoadingConfigurations] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [showAddForm, setShowAddForm] = useState(false);

    const [formData, setFormData] = useState({
        appId: "",
        appSlug: "",
        webhookSecret: "",
        organizationName: "",
        setupUrl: "https://bharathdev.whiteswansec.io/api/github/saas/auth/setup",
        instanceUrl: DEFAULT_INSTANCE_URL,
        privateKeyPem: "",
        privateKeyFileName: "",
    });

    const [submitting, setSubmitting] = useState(false);
    const [ctaMode, setCtaMode] = useState<CtaMode>("configure");
    const [installUrl, setInstallUrl] = useState<string>("");
    const [pendingConnection, setPendingConnection] = useState(false);

    const selectedConfig = useMemo(
        () => configurations.find((item) => String(item.id ?? `${item.tenantName}-${item.appId}-${item.organizationName}`) === selectedConfigId),
        [configurations, selectedConfigId]
    );

    const isFormValid =
        Boolean(tenantName) &&
        Boolean(formData.appId.trim()) &&
        Boolean(formData.appSlug.trim()) &&
        Boolean(formData.webhookSecret.trim()) &&
        Boolean(formData.organizationName.trim()) &&
        Boolean(formData.setupUrl.trim()) &&
        Boolean(formData.privateKeyPem.trim());

    const resetForm = () => {
        setFormData({
            appId: "",
            appSlug: "",
            webhookSecret: "",
            organizationName: "",
            setupUrl: "https://bharathdev.whiteswansec.io/api/github/saas/auth/setup",
            instanceUrl: DEFAULT_INSTANCE_URL,
            privateKeyPem: "",
            privateKeyFileName: "",
        });
        setInstallUrl("");
        setCtaMode("configure");
        setPendingConnection(false);
    };

    const loadTenants = async () => {
        try {
            const response = await fetchTenants();
            const tenantList = Array.isArray(response) ? response : [];
            setTenants(tenantList);

            if (!tenantName && tenantList.length > 0) {
                const firstTenant = tenantList[0]?.tenantName || "";
                setTenantName(firstTenant);
            }
        } catch {
            toast({
                title: "Failed to load tenants",
                description: "Unable to fetch tenant list.",
                variant: "destructive",
            });
        }
    };

    const loadConfigurations = async (targetTenantName: string) => {
        if (!targetTenantName) return;

        setLoadingConfigurations(true);
        try {
            const response = await githubGetConfiguration(targetTenantName);
            const list = Array.isArray(response) ? response : [];
            setConfigurations(list);

            if (list.length > 0) {
                const firstId = String(list[0].id ?? `${list[0].tenantName}-${list[0].appId}-${list[0].organizationName}`);
                setSelectedConfigId(firstId);
                setShowAddForm(false);
            } else {
                setSelectedConfigId("");
                setShowAddForm(true);
            }
        } catch (e: any) {
            setConfigurations([]);
            setSelectedConfigId("");
            setShowAddForm(true);
            toast({
                title: "Failed to load configuration",
                description: e?.response?.data?.message || "Unable to fetch GitHub configuration.",
                variant: "destructive",
            });
        } finally {
            setLoadingConfigurations(false);
        }
    };

    useEffect(() => {
        loadTenants();
    }, []);

    useEffect(() => {
        if (tenantName) {
            loadConfigurations(tenantName);
        }
    }, [tenantName]);

    const handlePemFile = (file: File) => {
        if (!file.name.toLowerCase().endsWith(".pem")) {
            toast({
                title: "Invalid file",
                description: "Please upload a .pem file.",
                variant: "destructive",
            });
            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            const content = String(e.target?.result || "");

            setFormData((prev) => ({
                ...prev,
                privateKeyPem: content,
                privateKeyFileName: file.name,
            }));

            toast({
                title: "PEM loaded",
                description: "Private key file parsed successfully.",
            });
        };

        reader.onerror = () => {
            toast({
                title: "Read failed",
                description: "Unable to read the PEM file.",
                variant: "destructive",
            });
        };

        reader.readAsText(file);
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        handlePemFile(file);
    };

    const handleConfigure = async () => {
        if (ctaMode === "connect") {
            if (!installUrl) return;

            window.open(installUrl, "_blank", "noopener,noreferrer");

            setSubmitting(true);
            try {
                await loadConfigurations(tenantName);
                setPendingConnection(false);
                setInstallUrl("");
                setCtaMode("configure");
                resetForm();

                toast({
                    title: "Connect flow started",
                    description: "Install URL opened. Configuration is now shown below.",
                });
            } finally {
                setSubmitting(false);
            }
            return;
        }

        if (!isFormValid) {
            toast({
                title: "Missing Information",
                description: "Please fill all required fields.",
                variant: "destructive",
            });
            return;
        }

        setSubmitting(true);
        setCtaMode("loading");

        try {
            const configurePayload = {
                tenantName,
                instanceUrl: formData.instanceUrl.trim() || DEFAULT_INSTANCE_URL,
                appId: Number(formData.appId),
                organizationName: formData.organizationName.trim(),
                appSlug: formData.appSlug.trim(),
                privateKeyPem: formData.privateKeyPem,
                webhookSecret: formData.webhookSecret.trim(),
                setupUrl: formData.setupUrl.trim(),
                syncEnabled: true,
            };

            await githubConfigure(configurePayload);

            const installResponse: InstallUrlResponse = await githubGetInstallUrl(
                tenantName,
                formData.organizationName.trim(),
                formData.instanceUrl.trim() || DEFAULT_INSTANCE_URL
            );

            if (!installResponse?.installUrl) {
                throw new Error("Install URL was not returned");
            }

            setInstallUrl(installResponse.installUrl);
            setPendingConnection(true);
            setCtaMode("connect");

            toast({
                title: "Configuration Saved",
                description: "Install URL created successfully. Click Connect to continue.",
            });
        } catch (e: any) {
            setCtaMode("configure");
            setPendingConnection(false);
            setInstallUrl("");
            toast({
                title: "Configuration Failed",
                description: e?.response?.data?.message || e?.message || "Unable to configure GitHub App.",
                variant: "destructive",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        const config = selectedConfig;
        if (!config || !tenantName) return;

        setDeleting(true);
        try {
            await githubDeleteConfiguration(
                tenantName,
                config.instanceUrl || DEFAULT_INSTANCE_URL,
                config.organizationName
            );

            toast({
                title: "Configuration Deleted",
                description: "GitHub configuration and synced data removed successfully.",
            });

            await loadConfigurations(tenantName);
            resetForm();
        } catch (e: any) {
            toast({
                title: "Delete Failed",
                description:
                    e?.response?.data?.message || "Unable to delete GitHub configuration.",
                variant: "destructive",
            });
        } finally {
            setDeleting(false);
        }
    };

    const shouldShowConfiguredState = !!configurations.length && !showAddForm && !pendingConnection;
    const shouldShowForm = showAddForm || configurations.length === 0 || pendingConnection;

    return (
        <div className="tenant-settings-page flex-1 space-y-6 p-6">
            <Card className="settings-header-card border-border/40 rounded-2xl">
                <CardContent className="p-5 sm:p-6">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="settings-back-button"
                            onClick={onBack}
                            aria-label="Back to settings"
                            title="Back to settings"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </Button>

                        <div className="settings-icon-frame w-12 h-12 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-xl flex items-center justify-center border border-slate-200 dark:border-slate-700">
                            <FaGithub className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>

                        <div className="flex-1">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                                        GitHub Setup
                                    </h1>
                                    <p className="text-muted-foreground">
                                        Configure GitHub App integration
                                    </p>
                                </div>

                                {shouldShowConfiguredState && (
                                    <Button
                                        onClick={() => {
                                            resetForm();
                                            setShowAddForm(true);
                                        }}
                                        className="gap-2"
                                        data-testid="button-add-github-configuration"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Another
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-border/40 rounded-2xl">
                <CardHeader>
                    <CardTitle>Tenant</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="max-w-md space-y-2">
                        <Label>Select Tenant</Label>
                        <Select value={tenantName} onValueChange={setTenantName}>
                            <SelectTrigger data-testid="select-tenant-name">
                                <SelectValue placeholder="Select tenant" />
                            </SelectTrigger>
                            <SelectContent>
                                {tenants.map((tenant) => (
                                    <SelectItem key={tenant.tenantName} value={tenant.tenantName}>
                                        {tenant.tenantName}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {shouldShowConfiguredState && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950 p-3 rounded-2xl border border-green-200 dark:border-green-800 shadow-sm">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">GitHub App configured successfully</span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {shouldShowConfiguredState && (
                    <div className="lg:col-span-2 space-y-4">
                        <div className="space-y-2">
                            {configurations.map((config) => {
                                const id = String(config.id ?? `${config.tenantName}-${config.appId}-${config.organizationName}`);
                                const isSelected = selectedConfigId === id;

                                return (
                                    <Card
                                        key={id}
                                        className={`cursor-pointer transition-colors hover-elevate rounded-2xl ${isSelected ? "border-blue-500 bg-blue-50 dark:bg-blue-950" : "border-border/40"}`}
                                        onClick={() => setSelectedConfigId(id)}
                                        data-testid={`card-github-config-${id}`}
                                    >
                                        <CardContent className="p-4 space-y-2">
                                            <div className="flex items-center justify-between gap-3">
                                                <div>
                                                    <div className="text-sm font-medium">{config.organizationName}</div>
                                                    <div className="text-xs text-muted-foreground">{config.appSlug}</div>
                                                </div>
                                                <Badge variant="secondary">#{config.appId}</Badge>
                                            </div>
                                            <div className="text-xs text-muted-foreground break-all">
                                                {config.instanceUrl || DEFAULT_INSTANCE_URL}
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className={shouldShowConfiguredState ? "lg:col-span-3" : "lg:col-span-5"}>
                    <Card className="border-border/40 rounded-2xl">
                        <CardHeader>
                            <CardTitle>
                                {shouldShowForm ? "Add GitHub App Configuration" : "Configuration Details"}
                            </CardTitle>
                        </CardHeader>

                        <CardContent className="space-y-5">
                            {loadingConfigurations ? (
                                <div className="py-10 text-center text-muted-foreground">
                                    Loading configuration...
                                </div>
                            ) : shouldShowForm ? (
                                <>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="github-app-id">App Id</Label>
                                            <Input
                                                id="github-app-id"
                                                value={formData.appId}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, appId: e.target.value }))}
                                                placeholder="App Id provided by GitHub when you create the GitHub App"
                                                data-testid="input-github-app-id"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="github-app-slug">App Slug</Label>
                                            <Input
                                                id="github-app-slug"
                                                value={formData.appSlug}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, appSlug: e.target.value }))}
                                                placeholder="GitHub App slug"
                                                data-testid="input-github-app-slug"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="github-webhook-secret">Webhook Secret</Label>
                                            <Input
                                                id="github-webhook-secret"
                                                value={formData.webhookSecret}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, webhookSecret: e.target.value }))}
                                                placeholder="Webhook secret"
                                                data-testid="input-github-webhook-secret"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="github-org-name">Organization Name</Label>
                                            <Input
                                                id="github-org-name"
                                                value={formData.organizationName}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, organizationName: e.target.value }))}
                                                placeholder="Organization name that the GitHub App is associated with"
                                                data-testid="input-github-organization-name"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="github-setup-url">Setup Url</Label>
                                            <Input
                                                id="github-setup-url"
                                                value={formData.setupUrl}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, setupUrl: e.target.value }))}
                                                placeholder="https://bharathdev.whiteswansec.io/api/github/saas/auth/setup"
                                                data-testid="input-github-setup-url"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="github-instance-url">Instance Url</Label>
                                            <Input
                                                id="github-instance-url"
                                                value={formData.instanceUrl}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, instanceUrl: e.target.value }))}
                                                placeholder={DEFAULT_INSTANCE_URL}
                                                data-testid="input-github-instance-url"
                                            />
                                            <div className="text-xs text-muted-foreground">
                                                Default is {DEFAULT_INSTANCE_URL}. You can override it manually here.
                                            </div>
                                        </div>
                                    </div>

                                    <Separator />

                                    <div className="space-y-3">
                                        <Label>Private Key PEM</Label>

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".pem"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                            data-testid="input-github-pem-file"
                                        />

                                        <div
                                            onClick={() => fileInputRef.current?.click()}
                                            className="settings-dropzone border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all border-muted hover:border-blue-400 hover:bg-muted/40"
                                            data-testid="dropzone-github-pem"
                                        >
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                                                    <Upload className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <div className="font-medium">Upload .pem file</div>
                                                    <div className="text-sm text-muted-foreground">
                                                        Click to choose the GitHub App private key from your device
                                                    </div>
                                                </div>
                                                {formData.privateKeyFileName && (
                                                    <div className="inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full bg-muted">
                                                        <FileKey2 className="w-4 h-4" />
                                                        {formData.privateKeyFileName}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {formData.privateKeyPem && (
                                            <div className="text-xs text-muted-foreground break-all">
                                                PEM parsed and stored in request-safe escaped format.
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-end gap-2 pt-2">
                                        {configurations.length > 0 && !pendingConnection && (
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    setShowAddForm(false);
                                                    resetForm();
                                                }}
                                                data-testid="button-cancel-github-add"
                                            >
                                                Cancel
                                            </Button>
                                        )}

                                        <Button
                                            onClick={handleConfigure}
                                            disabled={(!isFormValid && ctaMode !== "connect") || submitting}
                                            className="gap-2 min-w-[140px]"
                                            data-testid="button-configure-github"
                                        >
                                            {ctaMode === "loading" ? "Loading..." : ctaMode === "connect" ? "Connect" : "Configure"}
                                        </Button>
                                    </div>

                                    {ctaMode === "connect" && installUrl && (
                                        <div className="settings-muted-card rounded-lg border border-border/50 bg-muted/10 p-4 space-y-1">
                                            <div className="flex items-center gap-2 text-sm font-medium">
                                                <LinkIcon className="w-4 h-4" />
                                                Installation URL ready
                                            </div>
                                            <div className="text-xs text-muted-foreground break-all">
                                                {installUrl}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : selectedConfig ? (
                                <>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Tenant</Label>
                                            <Input value={selectedConfig.tenantName} readOnly />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Instance Url</Label>
                                            <Input value={selectedConfig.instanceUrl || DEFAULT_INSTANCE_URL} readOnly />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>App Id</Label>
                                            <Input value={String(selectedConfig.appId)} readOnly />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>App Slug</Label>
                                            <Input value={selectedConfig.appSlug} readOnly />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Organization Name</Label>
                                            <Input value={selectedConfig.organizationName} readOnly />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Status</Label>
                                            <Input value="Configured" readOnly />
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-2 pt-2">
                                        <Button
                                            variant="destructive"
                                            onClick={handleDelete}
                                            disabled={deleting}
                                            className="gap-2"
                                            data-testid="button-delete-github-configuration"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            {deleting ? "Deleting..." : "Delete Configuration"}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div className="py-10 text-center text-muted-foreground">
                                    No configuration found.
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
